// client/src/utils/invoiceLineMath.ts
//
// The arithmetic of a vendor invoice: what one line comes to, whether it agrees
// with what the supplier printed, and how much of the bill is not supported by
// the supplier's own quantities and prices.
//
// WHY THIS EXISTS
// ---------------
// This calculation used to be written twice — once here (inside lineItemFlags)
// and once in the server's vendorInvoiceProcessor — and the two disagreed about
// the one number that costs money.
//
// The server measured each line against whichever of the pre-VAT or the
// VAT-inclusive reading sat NEAREST the printed figure. This side first worked
// out, from the lines that do reconcile, which form the supplier prints in, and
// measured the odd line out against that. On the reported case — 10 packs at $10
// billed as $120, on an invoice whose other lines are plainly pre-VAT — the
// server recorded an overcharge of 7.50 and this grid showed 20.00.
//
// Nearest-fit is the wrong rule for an overcharge: it picks whichever reading
// makes the discrepancy smallest, which is backwards for a figure whose entire
// job is to stop an overpayment. The invoice's own convention is the right rule,
// and it is the one kept below.
//
// MIRRORED FILE: src/utils/invoiceLineMath.js is a line-for-line twin.
// CRA's ModuleScopePlugin forbids importing from src/, so the copy is the only
// option (the repo already does this for unitVocabulary and paymentTerms).
// Change one, change both.

// The one unit vocabulary, and a mirrored twin itself. Imported rather than
// re-spelled here: "CS", "cases" and "CASE" have to be one answer in this file
// for the same reason they are one answer everywhere else.
import {
  canonicalUnit,
  isSingleItemUnit,
  isDimensionUnit,
  normalizeUnit,
  readUnitNotation,
  measureFromText,
  splitSizeText,
  GROUPING_UNITS,
} from "./unitVocabulary";
// A mirrored twin too. The count-times-size reader and the one quantiser for a
// derived quantity, for `lineUnitSize` (prod USC-BUG-0030). packConversion reads
// only unitVocabulary, so this adds no cycle.
import { readCountTimesSize, roundQuantity } from "./packConversion";
import type { CountTimesSize } from "./packConversion";

// 1% of the figure, with a 50c floor so cent-rounding on small lines is not a
// discrepancy.
export const TOL_PCT = 0.01;
export const TOL_ABS = 0.5;

export function tolerance(expected: number): number {
  return Math.max(TOL_ABS, TOL_PCT * Math.abs(expected));
}

export function isEmptyValue(v: any): boolean {
  return v === undefined || v === null || String(v).trim() === "";
}

/**
 * A number off an invoice record, in any of the shapes one is stored in.
 *
 * 44 of the invoices in QA hold their tax rate as the string the document
 * printed — "12.50%", and older ones the bare flag "V". `Number("12.50%")` is
 * NaN, so those lines read as having no rate at all: the grid showed "12.50%"
 * in the cell, computed no tax against it, and raised no flag either, because
 * every check needs a number to compare. Percent signs, thousands separators
 * and currency marks are all noise around a figure, not part of it.
 */
export function num(v: any): number | null {
  if (isEmptyValue(v)) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;

  let s = String(v).trim();

  // Accounting negatives. "(1,234.56)" is -1234.56 wherever the convention is
  // used, and a credit line read as a positive charge is a sign error in the
  // direction that costs money.
  let negative = false;
  if (/^\((.*)\)$/.test(s)) {
    negative = true;
    s = s.slice(1, -1).trim();
  }

  // Currency marks, percent signs, and spaces used as group separators.
  s = s.replace(/[\s$%]/g, "");

  if (s.startsWith("-")) {
    negative = !negative;
    s = s.slice(1);
  }

  // Decimal comma — "1.234,56" and "1234,56". The comma is the decimal point
  // when it is the last separator with one or two digits behind it; otherwise
  // it groups thousands, which is the convention on the invoices this reads.
  // Stripping it unconditionally turned "1 234,56" into 123456.
  if (/^\d{1,3}(?:\.\d{3})+,\d{1,2}$/.test(s) || /^\d+,\d{1,2}$/.test(s)) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else {
    s = s.replace(/,/g, "");
  }

  if (!s || !/^\d*\.?\d+$/.test(s)) return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return negative ? -n : n;
}

export function round2(n: number): number {
  return Number(n.toFixed(2));
}

export function money(n: number): string {
  return Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Decimal places a QUANTITY keeps. Money is 2dp; a quantity is not money.
 *
 * Three, matching utils/vendorInvoiceSplit's QTY_DP, because the two describe
 * the same figure: what this line holds. A weight billed to a tenth of a kilo
 * survives at 3, and it is still coarse enough to absorb binary-float noise
 * (0.1 x 3 = 0.30000000000000004).
 */
export const QTY_DP = 3;

export function roundQty(n: number): number {
  const f = Math.pow(10, QTY_DP);
  const sign = n < 0 ? -1 : 1;
  return (sign * Math.round((Math.abs(n) + Number.EPSILON) * f)) / f;
}

/**
 * Quantity x Units per Case = Total Units.
 *
 * The one arithmetic behind the review grid's three linked columns. It was
 * written out THREE times — `linkPackMath` (the server's authority),
 * `packMathWrites` (the grid's immediate echo) and `getLineItemFlags` (the
 * check that says they disagree) — and all three rounded the product to a whole
 * number.
 *
 * That rounding fabricated stock. Meat, produce and cheese are billed by weight:
 * a line reading 10.6 KG stocked in as 11, and the 0.4 was invented by the
 * arithmetic rather than delivered by the supplier. Worse, the flag that exists
 * to catch a contradiction between the three columns rounded the same way, so it
 * agreed with the fabricated figure and stayed silent.
 *
 * Whole numbers are unchanged: an integer times an integer is an integer, and
 * `roundQty` only ever moves a product that was not one. Whether a fractional
 * receipt is PLAUSIBLE — 10.6 tins is not a thing — is a judgement about the
 * goods, and it belongs in the review flags, not in the multiplication.
 *
 * Returns null when either factor is missing or negative: a product of an
 * unknown is not a fact, and the callers each have their own answer to that.
 */
export function packMathTotalUnits(
  qty: number | null | undefined,
  packCount: number | null | undefined
): number | null {
  if (qty === null || qty === undefined) return null;
  if (packCount === null || packCount === undefined) return null;
  if (!Number.isFinite(qty) || !Number.isFinite(packCount)) return null;
  if (qty < 0 || packCount < 0) return null;
  return roundQty(qty * packCount);
}

/**
 * Pack units whose Units / Case is a fact the line MUST state.
 *
 * Asked for in the 2026-08-21 review call, and deliberately narrower than
 * "always". TECH_DEBT #72 settled Units / Case as OPTIONAL the day before, for
 * a reason that still holds: goods sold loose - shrimp by the pound, oil by the
 * gallon - state no pack size, and demanding one puts a red cell on most of the
 * corpus for a fact the invoice never claimed. What the call added is the other
 * half of that sentence. A line that says it bought a CASE has asserted there IS
 * a pack, and how many items that pack holds is then a fact the document has and
 * the record does not.
 *
 * What a blank actually costs, stated precisely because the obvious guess is
 * wrong: stock does NOT read this field. `buildUom` derives its pack ratio from
 * Total Units / Quantity and `buildStock` never looks at the pack count, so a
 * blank here moves no balance. What it costs is (a) the grid's own arithmetic -
 * Units / Case x Quantity is how Total Units is derived when a reviewer corrects
 * a quantity (`packMathTotalUnits`, `linkPackMath`), which is exactly the
 * calculation the call was about - and (b) pricing: this is
 * `pricingBasis.computeAtomicUnitsPerUom`'s `stated_pack_count` reading and the
 * master catalogue's `units_per_case`, so without it a per-piece quote falls
 * through to whatever `parsePackPattern` can recover from prose, or to nothing.
 *
 * WIDENED, D65 (owner, 2026-09-25). The scope above was deliberately CASE-only
 * on the day it was written - "widening it is one entry in this set, but do it
 * on a decision" - and the decision arrived: every `GROUPING_UNITS` pack (BOX,
 * CARTON, PACK, BUNDLE, TRAY, alongside CASE) plus BAG and SACK. The call was
 * three data-entry mistakes the old CASE-only rule could not catch at all: a
 * "5kg Bag" with 5 typed into Units / Case (the size, not a count), a BOX of
 * ice-cream bars left at 1 when it holds 10 (24 boxes -> 240 bars, not 24), and
 * a 100 lb tuna billed per PCS with 100 typed here.
 *
 * BAG and SACK are named explicitly rather than folded into `GROUPING_UNITS`
 * itself: that set stays exactly what `unitVocabulary`'s own header says it
 * must (D13/D20 - a bag is genuinely BOTH a single item and a grouping, and
 * classifying it there would move PRICING on every line quoted in BAG). This
 * requirement is a narrower, separate question - "does the reviewer owe a
 * count" - and BAG/SACK answer yes to it without joining GROUPING_UNITS.
 *
 * Built FROM `GROUPING_UNITS`, never a second hand-copied list: widening the
 * vocabulary's grouping set widens this for free, and the two can never list
 * BOX in one and not the other.
 */
export const PACK_COUNT_REQUIRED_UOMS: ReadonlySet<string> = new Set([...Array.from(GROUPING_UNITS), "BAG", "SACK"]);

/**
 * A pack unit whose Units / Case cannot be anything but 1 - `impliedPackCount`
 * says so already, by definition (a bottle holds one bottle; a kilogram of
 * pounds is a count of pounds). D65 (2026-09-25): the review grid takes that
 * as licence to LOCK the cell rather than merely imply the answer - it shows
 * "1", greyed, and refuses to let a reviewer type a case count onto a PCS.
 *
 * The lock is on the CELL, not on the FACT: a stored value of 1 is still never
 * written for such a line (D18 / TECH_DEBT #72 - fabricating one would enter
 * vendor item memory as a claim about the packaging the invoice never made).
 * Blank stays blank; only the display says "1".
 */
export function packCountLocked(line: any): boolean {
  return impliedPackCount(line) === 1;
}

/**
 * The line's outer selling unit, canonicalised.
 *
 * Reads the same ordered fallback `vendorInvoiceInventorySync.buildUom` reads
 * (`splitUow || uow || uom || unit`), so the requirement is decided from the
 * same string that ends up naming the pack in stock. Through `canonicalUnit`,
 * because a requirement that depended on the spelling would be enforced on the
 * invoices that print "CASE" and skipped on the ones that print "CS".
 */
export function linePackUnit(line: any): string | null {
  // `||`, not `??`: an EMPTY splitUow must fall through to the printed unit the
  // same way buildUom's fallback does. With `??` a line carrying
  // `splitUow: ''` and `uow: 'CASE'` answered null here while inventory named
  // the pack CASE — so the Units / Case requirement was skipped on exactly the
  // line that stocks in as a case (found by review, 2026-08-21).
  return canonicalUnit(line?.splitUow || line?.uow || line?.uom || line?.unit);
}

/**
 * Must this line state how many items its pack holds?
 *
 * A predicate, not a check: it says only whether an absence is a gap, never
 * whether the value is present. It lives here, in the mirrored twin, rather
 * than beside the review grid's flag list, because the same question has to be
 * answered identically on the server when the confirm gate learns to refuse an
 * incomplete line - a client-only copy is how "mandatory" comes to mean two
 * different things on the two sides of the wire.
 */
export function packCountRequired(line: any): boolean {
  const uom = linePackUnit(line);
  return uom !== null && PACK_COUNT_REQUIRED_UOMS.has(uom);
}

/**
 * How many items one pack holds when the UNIT WORD alone settles it.
 *
 * The complement of `packCountRequired`: that names the lines where the answer
 * has to come from the document, this names the lines where the document never
 * needed to state it.
 *
 * D&M Vegetables 28815 is the report - 38 lines of produce, every one billed by
 * the LBS, every one arriving with Total Units empty because `parsePackPattern`
 * has nothing to find in "Potatoes", and a reviewer asked to retype the
 * quantity into the cell beside it thirty-eight times. The red cell was wrong
 * about what a blank costs, too: `buildStock` falls back to the quantity, so
 * nothing was going to be skipped. What it actually costs is the pricing basis
 * (`computeAtomicUnitsPerUom` finds no ratio at all) and the typing.
 *
 * The answer the word gives is 1, and it is a DEFINITION rather than an
 * inference: one pound holds one pound, for exactly the reason
 * `unitVocabulary`'s SINGLE_ITEM_UNITS says one bottle holds one bottle. So
 * Total Units on such a line is the Quantity and may be derived.
 *
 * Read off the vocabulary's OWN two classifications, never a third list here:
 *   - a DIMENSION unit (LBS, KG, L, GAL, MTR...) - a quantity of pounds is a
 *     count of pounds;
 *   - a SINGLE_ITEM unit (PCS, BTL, TIN, JAR...) - the container IS the
 *     sellable item.
 *
 * Everything else answers null, and the silence is the point. CASE, BOX and
 * CARTON hold a number only the line can state. BAG, SACK, DOZ, SET, PRS,
 * LENGTH, SHEET and CUP are the units that vocabulary deliberately leaves
 * unclassified - D13 and D16 both turned on not guessing for them - and a unit
 * it has never met answers null in `linePackUnit` before it reaches here.
 *
 * IT DOES NOT AUTHORISE WRITING `splitPackCount`, and no caller may. That cell
 * is a claim about the PACKAGING and the invoice made none - TECH_DEBT #72
 * settled it as optional for exactly these lines - and it is a learnable
 * DERIVED field (`vendorItemProfileFields`), so a fabricated 1 would enter the
 * vendor item memory as a fact about the product and could later replace a real
 * pack count parsed off a case line. This answers the arithmetic's question
 * without putting a number on the paper's behalf.
 */
export function impliedPackCount(line: any): number | null {
  const uom = linePackUnit(line);
  if (uom === null) return null;
  return isSingleItemUnit(uom) || isDimensionUnit(uom) ? 1 : null;
}

/**
 * How big one item is when the PACK UNIT is itself a measure - "1 LBS" on a
 * line billed by the pound.
 *
 * The second half of D18, and it is DELIBERATELY NARROWER than
 * `impliedPackCount` above. That one accepts a single-item unit as well, because
 * "how many bottles in one bottle" has an answer. This one does not, because
 * "how big is one bottle" does NOT: the size of a bottle is 500 ML, a fact the
 * line has to state, and writing "1 BTL" into the Unit Size cell would fill it
 * with a non-size and silence the red flag that is correctly asking for the ML.
 * A pound, though, is a size — one pound — so the cell carries no information
 * the unit word has not already given.
 *
 * WHAT THIS MOVES, because it is more than a colour. `splitUnitSize` is read by
 * `itemGroup.weightTierFromSize` / `volumeTierFromSize`, which set
 * `weightPerLevel1` on the InventoryItem and the catalogue row, which is what
 * `costEngine.computeCostLevels` divides to get ttPerKg / usdPerKg. Today those
 * lines carry null and the per-kg tiers are simply not derivable - the "stock
 * held 5 CASES and nothing could say those were 75 kg" complaint from the
 * 2026-08-20 call. After this, 88 LBS at 3.50 states 7.735 per kg — through
 * USC's pound (`packConversion.LB_PER_KG` is 1 / 2.21 since D62.1, 2026-09-24;
 * it was 1 / 2.2 from 2026-09-07; NOT the physical 0.45359237), so it is
 * 3.50 x 2.21 exactly. Physics would say 7.7161; the gap is a stated business
 * choice and its header says not to correct it.
 *
 * `splitItemUow` follows from the same reading, and note what it does NOT do:
 * `pricingBasis.resolveCountUnitName` refuses a weight or volume word in a
 * COUNT-unit field (TECH_DEBT #50/#73 - a case of 14 pieces labelled unit "G"),
 * so `level1Name` stays PCS and only the weight TIER moves. That refusal is
 * load-bearing here rather than incidental: it is what keeps this from renaming
 * what stock counts.
 *
 * A length unit (MTR) is included and costs nothing: `parseSize` reports it as
 * neither mass nor volume, so both tiers stay null and only the red cell
 * clears. A fluid ounce is the one ounce (FLOZ / FL OZ read as OZ), so it lands
 * in the MASS tier; pricing reaches litres from it (a stated volume, else water), not this tier (D170.4).
 *
 * Returns `{ unitSize, unitUom }`, the same shape `parseUnitSize` returns, so a
 * caller formats the cell exactly as it formats a parsed one.
 */
export function impliedUnitSize(line: any): { unitSize: number; unitUom: string } | null {
  const uom = linePackUnit(line);
  if (uom === null || !isDimensionUnit(uom)) return null;
  return { unitSize: 1, unitUom: uom };
}

/**
 * THE PACK UNIT A LINE STARTS WITH, from the unit the supplier printed — what
 * fills a blank Pack Unit before any reviewer has looked at it.
 *
 * Two writers seed that cell: `vendorInvoiceProcessor.applySplitToLineItems` on
 * a fresh extraction, and the review page's `backfillSplitFields` on a line
 * processed before the split cells existed. The page's copy said it "mirrors the
 * processor exactly" and had stopped doing so: it wrote the printed cell as
 * typed, so a legacy "CS10PK" became a Pack Unit nobody can read (refused at
 * Confirm as UNIT_NOT_RECOGNISED) and lost its count of ten, where the processor
 * writes CASE and 10 (MC-4). One rule, here, in the mirrored twin, so the two
 * cannot part again.
 *
 *   - A unit the vocabulary knows is written canonical ("cs" -> CASE).
 *   - A cell that states a pack unit AND its count ("CS10PK", "CASE OF 10",
 *     "2DOZ") is that unit, and the count comes back as `unitWordPackCount` for
 *     the caller to offer where the description stated none. `readUnitNotation`
 *     owns the reading; a bare "24" names no unit and stays raw.
 *   - UNLESS the description's own pack count disagrees ("NOODLES 12X85G" in a
 *     "CS10PK" cell): then the cell stays raw, so it stays red and the grid names
 *     both counts. A clean CASE would remove the only signal (MC-4 review).
 *   - Anything else is kept as printed, never blanked: an unrecognised unit is
 *     the only evidence of what was misread. A blank stays blank — no "PACKS"
 *     is invented for a supplier who printed nothing (ClickUp 86d49xaxm).
 *
 * PURE.
 */
export function seedPackUnit(
  printed: unknown,
  descriptionPackCount: number | null | undefined,
  descriptionUnitSize?: string | null
): { splitUow: string; unitWordPackCount: number | null; unitSize?: string } {
  const raw: any = printed == null ? "" : printed;
  const known = normalizeUnit(raw);
  const notation = known ? null : readUnitNotation(raw);
  const notationPack = notation && notation.unit && notation.packCount ? notation : null;
  const packCount = descriptionPackCount || null;
  const disagrees = notationPack !== null && packCount !== null && notationPack.packCount !== packCount;
  // D100.1: "KG 5" is one PACK of 5 KG. The size the line already has outranks it
  // only by AGREEING; a different one leaves the cell raw so the gate asks.
  const sized = notation && notation.unit && notation.unitSize ? notation : null;
  const sizeDisagrees =
    sized !== null && !!descriptionUnitSize && !sameUnitSize(sized.unitSize as string, descriptionUnitSize);
  return {
    splitUow:
      known || (disagrees || sizeDisagrees ? null : (notationPack || sized) && (notationPack || sized)!.unit) || raw || "",
    // D100.3 (owner: "auto 1"): one pack of 5 KG holds one item, unless the
    // description states a count of its own.
    unitWordPackCount:
      packCount !== null ? null : notationPack ? notationPack.packCount : sized && !sizeDisagrees ? 1 : null,
    ...(sized && !sizeDisagrees ? { unitSize: sized.unitSize } : {}),
  };
}

// "5 KG" and "5kg" are one size: the measure each states, read by the one
// measure reader (`measureFromText`), compared by amount and canonical unit.
function sameUnitSize(a: string, b: string): boolean {
  const read = (s: string) => {
    const m = measureFromText(s);
    return m ? `${Number(m.amount)} ${m.unit}` : String(s).trim().toUpperCase();
  };
  return read(a) === read(b);
}

/**
 * The two answers to "how is this count-times-size Unit Size read?", as stored
 * on the line in `splitUnitSizeReading` (BUSINESS_RULES#unit-size-count-times-size):
 *
 *   count_times_size  one unit holds count x size: "20ct*1.5oz" is 30 OZ.
 *   each              the count is the case count: "12x28oz" is 28 OZ a unit.
 *
 * Here, in the mirrored twin, because both sides of the wire read it: the grid
 * offers exactly these two, and the server refuses a patch naming anything else.
 */
export const UNIT_SIZE_READINGS = ["count_times_size", "each"] as const;
export type UnitSizeReading = (typeof UNIT_SIZE_READINGS)[number];

/** What `lineUnitSize` says about one line. */
export type LineUnitSize = {
  size: unknown;
  countTimesSize: CountTimesSize | null;
  readings: Record<UnitSizeReading, string> | null;
  answer: UnitSizeReading | null;
  needsAnswer: boolean;
};

/**
 * THE SIZE OF ONE INNER UNIT, as a line's Unit Size states it once the reviewer
 * has said how to read it. Prod USC-BUG-0030 / 0031 / 0032, Sea Food
 * Enterprises 127632-12.
 *
 * A Unit Size shaped "20ct*1.5oz" can mean two things, and prod holds both:
 * twenty 1.5 oz pieces in each unit (30 OZ), or — "12x28oz" — twelve 28 oz cans
 * to the case, 28 OZ a unit. The owner's decision (2026-09-15) is to ASK on
 * review: nothing is guessed, and rows already in the catalogue do not change.
 *
 * Returns, for one line:
 *   size            the text every size reader is handed —
 *                   `itemGroup.weightTierFromSize` / `volumeTierFromSize` and
 *                   `masterCatalogueVendorSync.conversionsFromPackFacts`. The
 *                   answered reading ("30 OZ", "1.5 OZ") when there is one;
 *                   otherwise the cell EXACTLY as stored, so a line without the
 *                   shape, and one with the shape but no answer (every invoice
 *                   confirmed before the question existed), reads as it always did.
 *   countTimesSize  `packConversion.readCountTimesSize`'s reading, or null.
 *   readings        both answers as size text, for the question put to the
 *                   reviewer; null without the shape.
 *   answer          the stored answer, when it is one of UNIT_SIZE_READINGS.
 *   needsAnswer     the shape, and no answer. The review grid asks on this and
 *                   the confirm gate (`computeUnitSizeReadingGate`) refuses on
 *                   it — one function, so the two cannot disagree about a line.
 *
 * An answer beside a cell WITHOUT the shape is ignored rather than trusted: it
 * describes text that is no longer there. The product is rounded by
 * `packConversion.roundQuantity`, the one quantiser for a derived quantity, so
 * 3 x 0.1 L reads "0.3 L" and not float dust.
 */
export function lineUnitSize(line: any): LineUnitSize {
  const typed = line?.splitUnitSize;
  const shape = readCountTimesSize(typed);
  if (!shape) {
    return { size: typed, countTimesSize: null, readings: null, answer: null, needsAnswer: false };
  }
  const readings: Record<UnitSizeReading, string> = {
    count_times_size: `${roundQuantity(shape.count * shape.each)} ${shape.unit}`,
    each: `${shape.each} ${shape.unit}`,
  };
  const stored = line.splitUnitSizeReading;
  const answer: UnitSizeReading | null = (UNIT_SIZE_READINGS as readonly string[]).includes(stored)
    ? stored
    : null;
  return {
    size: answer ? readings[answer] : typed,
    countTimesSize: shape,
    readings,
    answer,
    needsAnswer: answer === null,
  };
}

/** One cell a line owes before it can be stocked in. */
export type MissingStockCell = { key: string; label: string; reason: string };

/**
 * Does this line's Unit Size state an actual WEIGHT, VOLUME or LENGTH — "3 KG",
 * "40 LB" — rather than a count ("10 PCS") or nothing readable at all?
 *
 * D65 rule 3's proof that a pack of one is really one: `splitSizeText` is the
 * one parser this codebase already has for "a plain number, then a unit word",
 * so this reads no prose and guesses at no shape — a bare unit ("KG") answers
 * false, same as it does for `unitVocabulary.SIZE_TEXT_RE`'s every other
 * reader.
 *
 * Read through `lineUnitSize(line).size` — the ANSWERED size — not the raw
 * `splitUnitSize` cell. A count-times-size Unit Size ("20ct*1.5oz") is not
 * itself "a plain number, then a unit word", so reading the raw cell refused
 * every such line whether or not a reviewer had answered the question: the
 * answered size (30 OZ) is exactly the kind of proof "40 LB" already counts
 * as, and `lineUnitSize` is the one place that already turns the shape plus
 * its stored reading into that size text.
 *
 * `trustEachAnswer` (default true) exists for rule 3 alone. An 'each' answer
 * ("12x28oz" is 28 OZ a unit, and the 12 is the CASE count) says the opposite
 * of "one item per pack": it is the reviewer naming 12 as the count, not 1.
 * Reading `.size` for such a line still returns "28 OZ" — a real dimension
 * size — so without this flag rule 3 would read that as proof of a pack of
 * one, when the reviewer's own answer says the pack holds 12. Rule 4 has no
 * such conflict — it is asking whether the count MATCHES the size, and an
 * 'each' answer is exactly as much "a size" for that comparison as a
 * 'count_times_size' one — so it passes nothing here and keeps trusting both.
 */
function lineStatesDimensionSize(line: any, { trustEachAnswer = true }: { trustEachAnswer?: boolean } = {}): boolean {
  const info = lineUnitSize(line);
  if (!trustEachAnswer && info.answer === 'each') return false;
  const parsed = splitSizeText(info.size);
  if (!parsed) return false;
  const unit = canonicalUnit(parsed.unit);
  return !!unit && isDimensionUnit(unit);
}

/**
 * Has a person confirmed the line's CURRENT Units / Case, specifically?
 *
 * `packCountConfirmed` (D65) is a NUMBER, not a boolean tick — the value the
 * reviewer looked at and vouched for — so it exempts rule 3 or rule 4 only
 * while it still equals `splitPackCount`. Edit the count again and the
 * confirmation is instantly stale, with no separate "unconfirm" step anyone
 * has to remember: a stale confirmation simply stops matching.
 */
function packCountConfirmedMatches(line: any): boolean {
  const packCount = num(line?.splitPackCount);
  const confirmed = num(line?.packCountConfirmed);
  return packCount !== null && confirmed !== null && confirmed === packCount;
}

/**
 * Does the ITEM'S OWN NAME independently state a pack of exactly `n` — "5 x
 * 5kg", "24X300G" — the D65 rule 4 exemption for a count that only LOOKS like
 * a miscopied size because the two genuinely agree?
 *
 * Reuses `packConversion.readCountTimesSize`, the one pure count-times-size
 * reader this file already imports for `lineUnitSize` — never a second parser,
 * and never a service reached into from a mirrored util. Read against
 * `itemName` first, `description` under it, the same fallback
 * `detectPricePerItem` uses for the same reason: the cleaned name is what a
 * reviewer typed over the printed text, so it is asked first.
 *
 * NARROWER than the D65 spec's own examples suggest, and deliberately so:
 * `readCountTimesSize`'s pattern is anchored (`^...$`) — it reads a cell that
 * IS "24X300G", never a word buried in "SWISS PENNE PASTA 24X300G". Loosening
 * it to scan prose is exactly what `packingAlert.packCountNamedIn`'s own
 * comment warns against for the same shape ("too loose to price with"), and
 * doing that here would be the new parser this rule was told not to write.
 * No PURE, already-mirrored util reads an embedded count out of a description,
 * so for an ordinary printed name this always answers false and
 * `packCountConfirmed` is the exemption a reviewer actually uses — this still
 * catches the degenerate case where the name/description IS the bare pattern.
 */
function descriptionStatesPackCount(line: any, n: number): boolean {
  const text = line?.itemName || line?.description;
  const shape = readCountTimesSize(text);
  return !!shape && shape.count === n;
}

/** "not the weight" / "not the volume" / "not the length" for rule 4's sentence. */
function sizeNoun(unit: string | null): string {
  if (unit === 'L' || unit === 'ML' || unit === 'GAL' || unit === 'PT') return 'the volume';
  if (unit === 'MTR') return 'the length';
  return 'the weight';
}

export type PackCountGuardIssue = { kind: 'sold_as_one' | 'matches_size'; reason: string };

/**
 * D65 rules 3 and 4 — the one check behind three things at once: the review
 * grid's red Units / Case cell, the pre-flight refusal `missingStockCells`
 * feeds, and the row's own "Sold as one item" / "Yes, N per pack is right"
 * button, which has to ask about exactly the question this answers or a click
 * on it would clear a refusal it never named.
 *
 * Null once nothing is wrong, INCLUDING once `packCountConfirmedMatches` — the
 * reviewer has looked at this exact count and vouched for it. A LOCKED line
 * (`packCountLocked`) is answered by rule 1 in `missingStockCells` instead:
 * such a line is never asked to prove a count, only refused for holding one at
 * all.
 *
 *   rule 3  a REQUIRED unit's Units / Case is exactly 1 with no size stated —
 *           "a BOX holding one item" needs either a size or a person's word.
 *   rule 4  ANY line whose Units / Case > 1 equals the number in its own
 *           dimension-unit Unit Size — the size cell typed into the count
 *           cell, "5 KG" beside a Units / Case of 5.
 */
export function packCountGuardIssue(line: any): PackCountGuardIssue | null {
  const packCount = num(line?.splitPackCount);
  if (packCount === null || packCount <= 0) return null;
  if (packCountLocked(line)) return null;
  if (packCountConfirmedMatches(line)) return null;

  if (packCount === 1) {
    if (!packCountRequired(line)) return null;
    // trustEachAnswer: false — an 'each' answer here means the reviewer named
    // the shape's COUNT (not 1) as the case count, which contradicts a Units /
    // Case of 1 rather than proving it. See `lineStatesDimensionSize`.
    if (lineStatesDimensionSize(line, { trustEachAnswer: false })) return null;
    const unit = linePackUnit(line) || 'pack';
    return {
      kind: 'sold_as_one',
      reason:
        `A ${unit} holding 1 item with no size stated — how many are inside? ` +
        `(24 boxes of 10 bars = 240). If the ${unit} really is sold as one item, enter its ` +
        `size in Unit Size or tick "Sold as one item".`,
    };
  }

  // Rule 4 asks of EVERY line, required or not (BAG, SACK, DOZ, an unclassified
  // unit) — a size copied into Units / Case is the same mistake whatever the
  // Pack Unit says.
  //
  // But only of a count a PERSON TYPED (`splitPackCountSource` 'manual', which
  // the grid stamps on every Units / Case edit and the server stamps on an
  // unstated one). The mistake is a human copying the size across; a count the
  // extractor read off the printed pack pattern is not that mistake. "COCA
  // COLA 12/12OZ" is parsed as a case of 12 of 12 OZ, and asking of it refused
  // every such line on sight — the anchored `descriptionStatesPackCount` below
  // cannot see a pattern embedded in a name.
  if (line?.splitPackCountSource === 'manual' && lineStatesDimensionSize(line)) {
    // The same ANSWERED size `lineStatesDimensionSize` just judged — never the
    // raw cell a second time, or a count-times-size line would pass the guard
    // above and then read null here (the raw cell alone does not parse).
    const parsed = splitSizeText(lineUnitSize(line).size) as { amount: string; unit: string };
    const amount = num(parsed.amount);
    const unit = canonicalUnit(parsed.unit);
    if (amount !== null && amount === packCount && !descriptionStatesPackCount(line, packCount)) {
      return {
        kind: 'matches_size',
        reason:
          `Units / Case ${packCount} matches the ${parsed.amount} ${unit} size — Units / Case ` +
          `counts separate items, not ${sizeNoun(unit)}.`,
      };
    }
  }

  return null;
}

/**
 * The pack facts a line has to state before its stock record can be trusted,
 * and which of them this line is missing.
 *
 * Two ORIGINAL rules, and BOTH are conditional on what the line itself claims.
 * That is the whole design: "Total Units is compulsory" cannot be a rule here,
 * because the pack-math refusal in `computePackMathGate` tells a reviewer in as
 * many words to CLEAR Total Units on goods the invoice sells loose - one item
 * per pack is the right reading for shrimp by the pound, and the extractor
 * legitimately leaves the cell empty whenever the description carries no pack
 * pattern. A gate demanding it everywhere would refuse the state the other gate
 * asks for, and the two would deadlock on the same line.
 *
 *   1. A required-unit line must say how many the pack holds
 *      (`packCountRequired`). Asked for in the 2026-08-21 call: "units per
 *      case are not mandatory unless the pack unit is case".
 *
 *   2. A line that STATES a pack of more than one must say how many items came
 *      in total. This is the miscount behind the same call's other complaint,
 *      and it is unguarded anywhere else: with Units / Case 24, Quantity 10 and
 *      Total Units blank, `buildStock` falls back to level1Qty = the QUANTITY,
 *      so 10 packs of 1 reach stock where the line says 240 items - and
 *      `computePackMathGate` cannot catch it, because it returns early when
 *      Total Units is not a positive number. The money is the worse half:
 *      costPerLevel1 is lineTotal / level1Qty, so every unit is costed 24x too
 *      high and that figure opens the FIFO layer.
 *
 * A stated pack of exactly 1 is deliberately NOT asked for a total: one item
 * per pack means Total Units equals the Quantity, which is precisely what
 * `buildStock`'s fallback already writes. Deborah's chicken leg quarters - a
 * case sold as a single unit - confirm with the cell empty and stock in
 * correctly.
 *
 * FOUR MORE RULES, D65 (owner, 2026-09-25) — obvious Units / Case mistakes
 * turned from a colour into a refusal, all on `splitPackCount` beside the two
 * above:
 *
 *   3. A LOCKED unit (`packCountLocked` — one item per pack, by definition)
 *      that somehow carries a count > 1 is impossible on its face: "a PCS
 *      cannot hold 24". The reviewer can still clear it or retype the Pack
 *      Unit; only a fabricated 1 stays forbidden (D18).
 *
 *   4. A required unit's Units / Case of exactly 1 with no size stated on the
 *      line, and nobody has confirmed it — "a BOX holding one item, how many
 *      are really inside?" (`packCountGuardIssue`, rule 3 there).
 *
 *   5. ANY line whose Units / Case > 1 equals the number in its own
 *      dimension-unit Unit Size — a size typed into the count cell, "5 KG"
 *      beside a Units / Case of 5 (`packCountGuardIssue`, rule 4 there).
 *
 * Returns the missing or wrong cells, most consequential first, each with the
 * sentence to put in front of a reviewer. One list, three readers: the review
 * grid's red cell, the grid's pre-flight refusal, and the server's confirm gate.
 */
export function missingStockCells(line: any): MissingStockCell[] {
  const out: MissingStockCell[] = [];
  const packCount = num(line?.splitPackCount);
  // `||`, not `??`, and the difference is a false refusal. `applySplitToLineItems`
  // spreads the original line and then writes `splitItemUnits: itemUnits ?? ""`,
  // so a record that already carried the legacy `itemUnits` ends up holding BOTH
  // — an empty new field beside a populated old one. Nullish coalescing stops at
  // the empty string and calls Total Units absent; `buildStock` reads through it
  // to the legacy value and stocks the goods in correctly. The gate would then
  // refuse a line that reaches stock with the right count.
  const totalUnits = num(line?.splitItemUnits || line?.itemUnits);

  // A line that stocks nothing in states nothing to be incomplete about. Same
  // exemption computeExpiryGate and computePackMathGate take, and it lives
  // HERE rather than in the confirm gate so the grid cannot raise a refusal the
  // server would not - two spellings of "does this line stock in" is how a
  // reviewer comes to be blocked by a screen the server was going to accept.
  //
  // Through `lineQuantity` — the owner, and what `buildStock` itself now reads
  // (2026-09-22) — so this stays exactly what the comment above always said it
  // had to be: what `buildStock` will actually write. This is still what keeps
  // the freight and header lines some vendors print out of the refusal - they
  // stock nothing today, and there is still no way to delete one (TECH_DEBT
  // #74). Until 2026-09-22 this was a local `splitQuantity || qty || quantity`,
  // falsy-fallback like buildStock used to be; once buildStock was fixed to
  // take a reviewer's stated 0 at face value (D44 #54 — "nothing arrived, bill
  // nothing"), the local copy here became exactly the drift this rule exists
  // to prevent: a CASE line corrected to Quantity 0 stocks nothing per
  // buildStock, but this still read `qty` (nonzero) and could refuse the
  // confirm over Units / Case or Total Units a zero-quantity line owes nobody.
  const quantity = lineQuantity(line);
  if (!(quantity !== null && quantity > 0) && !(totalUnits !== null && totalUnits > 0)) return out;

  if (packCount !== null && packCount < 0) {
    // D65 (owner, 2026-09-25: "why are we allowing negative count"): a pack
    // holding fewer than no items is impossible whatever the Pack Unit, and no
    // other gate in `vendorInvoiceInventorySync.service.js` refuses it — the
    // grid's NON_NEGATIVE_CELLS only colours the cell. Before this, a DOZ, SET
    // or blank-unit line carrying -5 confirmed straight through.
    out.push({
      key: 'splitPackCount',
      label: 'Units / Case',
      reason: 'cannot be negative — enter how many items one pack holds, or clear it',
    });
  } else if (packCountLocked(line)) {
    // D65 rule 3: a one-item unit cannot hold more than one. The reviewer's
    // way out is the cell itself — it is editable exactly because it is wrong
    // (see the client's `packCountLocked` lock, which only locks a blank/1).
    if (packCount !== null && packCount > 1) {
      const unit = linePackUnit(line) || 'this unit';
      out.push({
        key: 'splitPackCount',
        label: 'Units / Case',
        reason:
          `a ${unit} cannot hold ${packCount}. Change the Pack Unit to CASE/BOX if it was ` +
          'sold by the pack, or clear this',
      });
    }
  } else if (packCountRequired(line) && !(packCount !== null && packCount > 0)) {
    // Named by the line's own unit: since D65 this asks of a BOX or a BAG too,
    // and "bought by the case" would describe a line that was not.
    const unit = linePackUnit(line);
    out.push({
      key: 'splitPackCount',
      label: 'Units / Case',
      reason:
        `the Pack Unit is ${unit}, so how many items one ${unit} holds has to be ` +
        `stated (1 if the ${unit} is sold as a single item)`,
    });
  } else {
    // D65 rules 4 and 5: a pack of one with no proof, or a size typed into the
    // count. Both are refused only until a person answers — a fresh confirm
    // clears whichever one fired, and going stale re-raises it automatically.
    const guard = packCountGuardIssue(line);
    if (guard) {
      out.push({ key: 'splitPackCount', label: 'Units / Case', reason: guard.reason });
    }
  }

  // D65 item 4: a LOCKED line already carries rule 3's refusal above when its
  // count is impossible ("a PCS cannot hold 24 ... clear this"), which is the
  // reviewer's whole instruction. Demanding Total Units for the same
  // impossible count on top of that is misleading advice, not a second real
  // gap — the line has to lose the count (or the Pack Unit) before Total
  // Units means anything, so it is never asked for on a locked line.
  if (
    !packCountLocked(line) &&
    packCount !== null &&
    packCount > 1 &&
    !(totalUnits !== null && totalUnits > 0)
  ) {
    out.push({
      key: 'splitItemUnits',
      label: 'Total Units',
      reason:
        `this line states ${packCount} items per pack, and without Total Units the ` +
        'stock record falls back to counting the packs — so the individual items ' +
        'would be undercounted and every one of them overcosted',
    });
  }

  return out;
}

/**
 * THE CELLS EVERY VENDOR-INVOICE LINE OWES, whatever it carries. QA USC-BUG-0061
 * / 0064: a line with a blank Unit Size was saved, confirmed and reached the
 * Master Catalogue, because this list lived only in the review grid, where it
 * coloured cells red and gated nothing (it closes TECH_DEBT #103).
 *
 * One list, three readers: the grid's red cell and column star
 * (`lineItemFlags`), the save (`vendorInvoice.service.assertRequiredLineCells`)
 * and Confirm & sync (`computeRequiredCellsGate`), which refuses before the
 * claim and so before the catalogue sync the confirm schedules - that gate IS
 * catalogue admission for a new invoice. `error` cells are refused; the `warn`
 * entry (Item Code) is advice and gates nothing.
 *
 * Read exactly as the grid reads them - the named key only, no legacy alias -
 * so a red cell and a refusal can never disagree about a line. Owner's decision
 * 2026-09-22: a freight or header line that has none of these is REMOVED
 * (remove-invoice-line), not exempted.
 */
export type RequiredLineCell = {
  key: string;
  label: string;
  level: 'error' | 'warn';
  reason: string;
};

export const REQUIRED_LINE_CELLS: RequiredLineCell[] = [
  {
    key: 'itemName',
    label: 'Item Name',
    level: 'error',
    reason: 'Item Name is empty — this line cannot be matched to a catalogue item.',
  },
  {
    key: 'splitUow',
    label: 'Pack Unit',
    level: 'error',
    reason: 'Pack Unit is empty — how the goods were sold (case, box, bag) could not be read.',
  },
  {
    key: 'splitQuantity',
    label: 'Quantity',
    level: 'error',
    reason: 'Quantity is empty — no amount could be read for this line.',
  },
  {
    key: 'splitItemUnits',
    label: 'Total Units',
    level: 'error',
    reason: 'Total Units is empty — this line will be skipped when stock is received.',
  },
  {
    key: 'splitUnitSize',
    label: 'Unit Size',
    level: 'error',
    reason: 'Unit Size is empty — the size of each item could not be read from the description.',
  },
  {
    key: 'splitItemUow',
    label: 'Unit Size unit',
    level: 'error',
    reason: 'The unit of the pack size is empty — enter the size as e.g. "500 ML".',
  },
  {
    key: 'unitPrice',
    label: 'Unit Price',
    level: 'error',
    reason: 'Unit Price is empty — this line has no price to check the totals against.',
  },
  {
    key: 'lineTotal',
    label: 'Line Total',
    level: 'error',
    reason: 'Line Total is empty — nothing to reconcile this line against the invoice total.',
  },
  {
    key: 'itemCode',
    label: 'Item Code',
    level: 'warn',
    reason: 'Item Code is empty. Not required, but it is how repeat purchases are matched.',
  },
];

/** Empty, as the grid means it: "" and null are gaps, a real 0 is a value. */
export function isMissingCell(v: any): boolean {
  if (v === 0) return false;
  return isEmptyValue(v);
}

/**
 * The cells a MONEY-ONLY check still requires (D95: a company expense stocks nothing,
 * so it keeps these and skips the pack / stock cells). Until the owner decides
 * otherwise. Pass as `onlyKeys` to `missingRequiredCells`.
 */
export const MONEY_REQUIRED_KEYS: string[] = ['itemName', 'splitQuantity', 'unitPrice', 'lineTotal'];

/**
 * The error-level required cells this line leaves empty, in list order.
 * `onlyKeys` narrows the rule to those cells; omitted = every required cell.
 */
export function missingRequiredCells(line: any, onlyKeys: string[] | null = null): { key: string; label: string; reason: string }[] {
  return REQUIRED_LINE_CELLS.filter(
    (c) => c.level === 'error' && (!onlyKeys || onlyKeys.includes(c.key)) && isMissingCell(line?.[c.key])
  ).map(({ key, label, reason }) => ({ key, label, reason }));
}

/**
 * The fields that move a line's VAT: the two tax columns, and the three inputs
 * to its taxable base.
 *
 * It lives in the mirrored twin, rather than beside either of the two things
 * that gate on it, because both sides of the wire read it. The server decides
 * from this list which lines to re-derive after a patch; this grid decides from
 * the SAME list whether an edit it just sent has a server-computed counterpart
 * worth reloading for. Written out twice, widening the server's list silently
 * stops the grid refreshing the very field it was widened for — the reviewer
 * then sees a stale rate beside a corrected quantity, and no reason to doubt it.
 *
 * `splitQuantity` is in the list because it is the field this grid's Quantity
 * column writes; `qty`/`quantity`/`unitPrice` because they move the base a
 * stated rate is re-applied to.
 */
export const TAX_LINE_FIELDS = ["taxRate", "taxAmount", "qty", "quantity", "splitQuantity", "unitPrice"];

/**
 * The fields that move a line's PACK arithmetic: the three linked quantity
 * columns, under every spelling a quantity arrives in.
 *
 * Here, beside TAX_LINE_FIELDS, and for the same reason — both sides of the
 * wire read it. The server decides from this list which lines to re-derive
 * after a patch (`linkPackMath`, and `linkPricePerItemRebase` behind it); this
 * grid decides from the SAME list whether the edit it just sent has a
 * server-computed counterpart worth reloading for.
 *
 * That mattered the moment the rebase moved onto the edit path. Correcting
 * Units / Case can now change the Unit Price two columns away — and while this
 * list lived only in the server, the grid had no reason to reload, so the
 * reviewer fixed the pack size and went on reading the price the server had
 * just replaced.
 */
export const PACK_LINE_FIELDS = ["splitPackCount", "splitQuantity", "splitItemUnits", "qty", "quantity"];

/**
 * The amount before VAT inside a VAT-inclusive total, at a rate — NOT rounded.
 * The one place "take the VAT out" is written. Null when either side is missing.
 */
export function netOfInclusiveTax(total: any, rate: any): number | null {
  const t = num(total);
  const r = num(rate);
  if (t === null || r === null) return null;
  return t / (1 + r / 100);
}

/**
 * The VAT a base owes at a rate, to the cent; null when either side is missing.
 * `inclusive: true` says the figure already holds the VAT (a no-price line's
 * printed total on a VAT-inclusive invoice — `votedConvention`): 112.50 at
 * 12.5% owes 12.50, not 14.06.
 */
export function taxAmountForRate(base: any, rate: any, { inclusive = false }: { inclusive?: boolean } = {}): number | null {
  const b = num(base);
  const r = num(rate);
  if (b === null || r === null) return null;
  if (inclusive) return round2(b - (netOfInclusiveTax(b, r) as number));
  return round2((b * r) / 100);
}

/**
 * The quantity of a line.
 *
 * `splitQuantity` FIRST: it is the field the review grid's Quantity column
 * writes to, so on an invoice a human has corrected it is the current value and
 * `qty` is what the extractor originally read.
 */
export function lineQuantity(line: any): number | null {
  return num(line?.splitQuantity ?? line?.qty ?? line?.quantity);
}

export type LineMath = {
  qty: number | null;
  unitPrice: number | null;
  discount: number;
  taxRate: number | null;
  taxAmount: number | null;
  lineTotal: number | null;
  /** qty x unit price, less the discount read the way the printed total proves. */
  base: number | null;
  /**
   * Every defensible reading of the taxable amount.
   *
   * The extractor's `discount` column carries no units: on one supplier it is
   * "50.00 off", on the next it is "10" meaning 10%, and the extraction prompt
   * does not distinguish them. Rather than pick a reading and paint half the
   * discounted lines red for disagreeing with a guess, a value is accepted if it
   * matches ANY of these — a discrepancy then means the figures really do not
   * reconcile under any interpretation.
   */
  bases: number[];
  /** The discount read PER UNIT (qty x discount off the gross), or null when
   *  there is no discount, no quantity, or it would flip the line's own sign. */
  perUnitBase: number | null;
  /** Which MEANING of `discount` the printed total proves — for a display
   *  (`lineDiscountAmount`), never for `base` itself. null when unproven or
   *  ambiguous (two different-meaning readings both match). */
  provenReading: "wholeLine" | "perUnit" | "percent" | null;
  /** What the rate says the VAT should be, when both the rate and a base exist. */
  expectedTax: number | null;
  /** The line total with VAT added — the other form suppliers legitimately print. */
  expectedTotalIncVat: number | null;
};

export type DiscountReadings = {
  wholeLine: number | null;
  perUnit: number | null;
  percent: number | null;
};

/**
 * Every defensible reading of a line's `discount` column off its own `gross`
 * (qty x price, before any discount) — the one owner of this arithmetic.
 * Shared by `computeLineMath`'s `bases` (the review grid) and the server's
 * `invoiceVat.taxableBaseReadings` (VAT resolution at ingestion), which used
 * to each carry their own copy: the ingestion copy had no per-unit reading at
 * all, so a USC-BUG-0070-shaped line (24 cases at 46.00 less 6.50 A CASE) could
 * reconcile on the review screen yet still fail to recover a VAT rate when the
 * invoice was first read.
 *
 * `discount` carries no units: on one supplier it is "50.00 off" (a WHOLE-LINE
 * amount), on the next "6.50" is that much PER UNIT, on a third "10" means a
 * PERCENT. All three are offered; the caller decides which one the printed
 * total proves.
 *
 * The per-unit reading is refused when it would flip `gross`'s own sign — a
 * per-unit discount bigger than the price is not this line's discount column
 * read the other way, it is some other column entirely.
 *
 * A "DIS PR." / "NET" column printing the price AFTER a discount is not read
 * here: the extraction prompt stores it as list price plus a per-unit
 * reduction, which the per-unit reading proves (D83, Blue Waters 3723225809).
 */
export function discountReadings(gross: number | null, qty: number | null, discount: number): DiscountReadings {
  const wholeLine = gross === null ? null : round2(gross - discount);
  const percent =
    gross !== null && discount > 0 && discount <= 100 ? round2(gross * (1 - discount / 100)) : null;

  let perUnit: number | null = null;
  // `qty !== null`, not the truthy `qty` this read until the D44 #48 probe
  // (invariant 8 — missing is not zero): a qty of 0 is a real quantity ("this
  // line's goods never arrived"), and its per-unit discount is legitimately 0
  // too (gross itself is 0 x price = 0, so nothing is owed either way). The
  // truthy read skipped the reading entirely on such a line, so a quantity
  // correction to 0 fell through to `wholeLine` — the flat discount amount,
  // still owed against goods that no longer exist — and invented a negative
  // lineTotal nobody billed.
  if (gross !== null && discount && qty !== null) {
    const candidate = round2(gross - qty * discount);
    const flipsSign = (gross > 0 && candidate < 0) || (gross < 0 && candidate > 0);
    if (!flipsSign) perUnit = candidate;
  }

  return { wholeLine, perUnit, percent };
}

/**
 * The arithmetic of one line, in one place, so the grid, the totals panel, the
 * flags and the stored validation cannot each work it out slightly differently.
 */
export function computeLineMath(line: any): LineMath {
  const qty = lineQuantity(line);
  const unitPrice = num(line?.unitPrice);
  const discount = num(line?.discount) ?? 0;
  const taxRate = num(line?.taxRate);
  const taxAmount = num(line?.taxAmount);
  const lineTotal = num(line?.lineTotal ?? line?.total ?? line?.amount);

  const gross = qty !== null && unitPrice !== null ? round2(qty * unitPrice) : null;
  const { wholeLine: wholeLineBase, perUnit: perUnitBase, percent: percentBase } = discountReadings(
    gross,
    qty,
    discount
  );

  // The other readings: the discount as a percentage, and it belonging to
  // some other column after all — plus the per-unit reading, pushed LAST so
  // `bases`' INDEX (load-bearing for the server's
  // `vendorLineCorrection.totalReading`, D44 #48) never moves for an existing
  // reading just because a new one became available on a line.
  const bases: number[] = [];
  if (gross !== null) {
    bases.push(wholeLineBase as number);
    if (discount) {
      if (percentBase !== null) bases.push(percentBase);
      bases.push(gross);
      if (perUnitBase !== null) bases.push(perUnitBase);
    }
  }

  // A defaulted or unresolved rate is a suggestion, not a fact — deriving from
  // it would manufacture a figure the invoice never stated, which is the exact
  // fabrication utils/invoiceVat.js exists to prevent.
  const rateIsStated =
    taxRate !== null && line?.taxRateDefaulted !== true && line?.taxRateUnresolved !== true;

  // `base`: which of the readings above the PRINTED line total actually
  // proves — whole-line amount, else per-unit amount. Tested against both the
  // exclusive and the VAT-inclusive form of each reading, since a supplier may
  // print either. No lineTotal, no discount, or neither of the two provable:
  // `base` stays the whole-line reading, exactly as it always has.
  //
  // NARROWER than `bases` on purpose (D82 pre-push review, item 3): percent and
  // gross stay OUT of this search. `base` is not a display list — it is the
  // figure every money consumer reads (edit-path taxAmount in
  // vendorInvoice.service.js, vendorLineCredit caps, conventionVotes), and
  // letting it drift to "percent" or "gross" whenever the printed total
  // happened to match one of them silently moved stored money beyond the
  // USC-BUG-0070 fix's own scope: a line with NO per-unit discount now reads
  // `base` byte-identical to before that fix (`round2(gross - discount)`,
  // origin/qa) even when it matches nothing the search tries. Reconciliation
  // (does the line check out at all) is unaffected — `checkLineTotal` still
  // tests the printed total against every reading in `bases`, percent and
  // gross included.
  let base = wholeLineBase;
  if (discount && lineTotal !== null) {
    const rate = taxRate;
    const vatOn = (b: number) =>
      rateIsStated && rate ? (taxAmountForRate(b, rate) as number) : taxAmount !== null ? taxAmount : 0;
    for (const b of [wholeLineBase, perUnitBase]) {
      if (b === null) continue;
      const incVat = round2(b + vatOn(b));
      if (matchesAny(lineTotal as number, incVat !== b ? [b, incVat] : [b])) {
        base = b;
        break;
      }
    }
  }

  // `provenReading` (D83 redesign, prod vendor-invoice 3723225809, Blue
  // Waters): which MEANING of `discount` the printed total actually proves —
  // for `lineDiscountAmount` and `checkLineTotal`'s wording, never for `base`
  // itself, which stays the narrower search above. See `provenDiscountReading`.
  const provenReading = provenDiscountReading({
    gross,
    qty,
    discount,
    lineTotal,
    taxRate,
    taxAmount,
    rateIsStated,
  });

  const expectedTax = rateIsStated ? taxAmountForRate(base, taxRate) : null;

  const vat = expectedTax !== null ? expectedTax : taxAmount !== null ? taxAmount : 0;
  const expectedTotalIncVat = base !== null ? round2(base + vat) : null;

  return {
    qty,
    unitPrice,
    discount,
    taxRate,
    taxAmount,
    lineTotal,
    base,
    bases,
    perUnitBase,
    provenReading,
    expectedTax,
    expectedTotalIncVat,
  };
}

/**
 * Which MEANING of `discount` the printed `lineTotal` proves — whole-line
 * amount, per-unit rate, or percent — for a DISPLAY that has to show what a
 * line actually loses (`lineDiscountAmount`), not for `base` itself.
 *
 * Deliberately BROADER than `base`'s own search above: this one also tries
 * percent, because a display figure carries none of the stability risk that
 * keeps percent out of `base` (D82 pre-push review, item 3). Two DIFFERENT
 * readings that both match the printed total, at two different money values,
 * are ambiguous — null (missing is not zero, invariant 8) rather than
 * guessing. A tie at the SAME value (whole-line and per-unit agree exactly at
 * qty 1) is not ambiguous: either name is returned.
 */
function provenDiscountReading({
  gross,
  qty,
  discount,
  lineTotal,
  taxRate,
  taxAmount,
  rateIsStated,
}: {
  gross: number | null;
  qty: number | null;
  discount: number;
  lineTotal: number | null;
  taxRate: number | null;
  taxAmount: number | null;
  rateIsStated: boolean;
}): LineMath["provenReading"] {
  if (!discount || lineTotal === null || gross === null) return null;
  const { wholeLine, perUnit, percent } = discountReadings(gross, qty, discount);
  const vatOn = (b: number) =>
    rateIsStated && taxRate ? (taxAmountForRate(b, taxRate) as number) : taxAmount !== null ? taxAmount : 0;
  const matches: Array<[NonNullable<LineMath["provenReading"]>, number]> = [];
  const candidates: Array<[NonNullable<LineMath["provenReading"]>, number | null]> = [
    ["wholeLine", wholeLine],
    ["perUnit", perUnit],
    ["percent", percent],
  ];
  for (const [reading, b] of candidates) {
    if (b === null) continue;
    const incVat = round2(b + vatOn(b));
    if (matchesAny(lineTotal as number, incVat !== b ? [b, incVat] : [b])) matches.push([reading, b]);
  }
  if (!matches.length) return null;
  if (new Set(matches.map(([, b]) => b)).size > 1) return null;
  return matches[0][0];
}

/**
 * This line's TOTAL discount, in money — whatever the printed `lineTotal`
 * PROVES it to be, not what `discount` happens to spell. D83, prod
 * vendor-invoice 3723225809 (Blue Waters), USC-BUG-0068/0070: the owner's
 * complaint was never that `discount` is stored wrong — it is stored exactly
 * as printed, on purpose (see the server twin's header comment: a quantity
 * correction, `vendorLineCorrection.correctLineExtension`, D44 #48, depends
 * on that being true) — the complaint was that every DISPLAY of the cell
 * showed the printed per-case/percent figure (6.50, 8.734%) instead of what
 * the line actually loses (156.00, gross x pct). This is that number, for a
 * DISPLAY to show beside the printed figure; nothing writes it back.
 *
 * Reads `computeLineMath`'s own `provenReading` — the SAME ambiguity-checked
 * search, never a second matcher — and returns null (never 0) when there is
 * no discount at all, or when nothing proves which reading is right (missing
 * is not zero, invariant 8).
 */
export function lineDiscountAmount(line: any): number | null {
  const m = computeLineMath(line);
  if (m.provenReading === null) return null;
  const gross = round2((m.qty as number) * (m.unitPrice as number));
  const { wholeLine, perUnit, percent } = discountReadings(gross, m.qty, m.discount);
  const base = { wholeLine, perUnit, percent }[m.provenReading];
  return base === null ? null : round2(gross - base);
}

/** Does `value` match any of the readings, within the usual tolerance? */
export function matchesAny(value: number, candidates: number[]): boolean {
  return candidates.some((c) => Math.abs(value - c) <= tolerance(c));
}

/**
 * The tax one line carries, whichever way the invoice states it.
 * A figure the supplier printed outranks one derived from a stated rate.
 */
export function lineVat(m: LineMath): number {
  if (m.taxAmount !== null) return m.taxAmount;
  if (m.expectedTax !== null) return m.expectedTax;
  return 0;
}

/** Does this line say anything at all about tax? */
export function lineStatesTax(m: LineMath): boolean {
  return m.taxAmount !== null || m.expectedTax !== null;
}

/**
 * Does this supplier print line extensions before or after VAT?
 *
 * It matters for money, not just for pass/fail. A line of 10 x $10 billed at
 * $120 is $20 too much on a tax-exclusive invoice and $7.50 too much on a
 * tax-inclusive one, and telling the client "you were overcharged" means naming
 * the figure. Rather than guess, ask the invoice: whichever form its lines
 * mostly reconcile in is the form it prints, and the odd line out is measured
 * against that.
 *
 * Defaults to exclusive, which is how these suppliers print — VAT once, at the
 * foot.
 */
export type Convention = "exclusive" | "inclusive";

export function invoiceConvention(lines: any[]): Convention {
  const { exclusive, inclusive } = conventionVotes(lines);
  return inclusive > exclusive ? "inclusive" : "exclusive";
}

/**
 * How many lines reconcile in each form — the votes `invoiceConvention` counts.
 * Both zero means no priced line said anything, and the 'exclusive' answer is
 * then a default rather than a reading (the server's `impliedUnitPrice` needs
 * to know).
 */
export function conventionVotes(lines: any[]): { exclusive: number; inclusive: number } {
  let exclusive = 0;
  let inclusive = 0;

  for (const li of Array.isArray(lines) ? lines : []) {
    const m = computeLineMath(li);
    if (m.base === null || m.lineTotal === null) continue;
    // A line carrying no VAT — zero-rated, or no rate or amount to go on —
    // prints the same total either way, so it says nothing about the
    // convention. Counted as "exclusive" (tested first) it outvoted the taxed
    // lines on every mixed invoice with more 0% lines than taxed ones (review
    // 2026-09-26). An invoice of nothing but such lines casts no vote at all.
    if (m.expectedTotalIncVat === m.base) continue;
    if (matchesAny(m.lineTotal, m.bases)) {
      exclusive += 1;
    } else if (
      m.expectedTotalIncVat !== null &&
      Math.abs(m.lineTotal - m.expectedTotalIncVat) <= tolerance(m.expectedTotalIncVat)
    ) {
      inclusive += 1;
    }
  }

  return { exclusive, inclusive };
}

/**
 * The convention the supplier's own prices vote for, or null when none votes.
 * A price worked out from the line total (`unitPriceFromLineTotal`) is ours
 * and casts no vote. null means "unknown": the no-price tax base keeps its
 * exclusive reading.
 */
export function votedConvention(lines: any[]): Convention | null {
  const printed = (Array.isArray(lines) ? lines : []).filter((l) => l?.unitPriceFromLineTotal !== true);
  const votes = conventionVotes(printed);
  return votes.exclusive || votes.inclusive ? invoiceConvention(printed) : null;
}

export type LineTotalCheck = {
  ok: boolean;
  expected: number;
  candidates: number[];
  message: string;
};

/**
 * Does the printed line total agree with qty x price (in any of the forms a
 * supplier legitimately prints)?
 *
 * Returns null when the line does not carry enough to judge.
 */
export function checkLineTotal(m: LineMath): LineTotalCheck | null {
  if (m.base === null || m.lineTotal === null || !m.bases.length) return null;

  const rate = m.taxRate;
  const vatOf = (b: number) =>
    m.expectedTax !== null && rate ? round2((b * rate) / 100) : m.taxAmount !== null ? m.taxAmount : 0;

  // Most suppliers print the pre-VAT extension and add VAT once at the foot,
  // some print it VAT-inclusive. Flagging the honest half of them would train
  // ops to ignore the colour, so both forms of every base reading count.
  const candidates: number[] = [];
  for (const b of m.bases) {
    candidates.push(b);
    const incVat = round2(b + vatOf(b));
    if (incVat !== b) candidates.push(incVat);
  }

  const ok = matchesAny(m.lineTotal, candidates);
  const expected = candidates.reduce(
    (acc, c) => (Math.abs((m.lineTotal as number) - c) < Math.abs((m.lineTotal as number) - acc) ? c : acc),
    candidates[0]
  );

  const incVatNote =
    m.expectedTotalIncVat !== null && m.expectedTotalIncVat !== m.base
      ? ` (or ${money(m.expectedTotalIncVat)} including VAT)`
      : "";

  const discountNote = m.discount ? ` less ${money(m.discount)} discount` : "";

  return {
    ok,
    expected,
    candidates,
    message: `${m.qty} x ${money(m.unitPrice as number)}${discountNote} = ${money(m.base)}${incVatNote}, but Line Total says ${money(m.lineTotal)}.`,
  };
}

export type PricePerItem = {
  qty: number;
  units: number;
  unitPrice: number;
  unitsPerPack: number;
  impliedPackPrice: number;
  statedPackCount: number | null;
  message: string;
};

/**
 * "This price is per ITEM, and the quantity beside it counts PACKS."
 *
 * The Master Catalogue costs one item by dividing Unit Price by Units / Case,
 * so Unit Price has to be the price of a PACK — the thing the Quantity column
 * counts. Most suppliers print it that way. West Indian Traders print a price
 * per item against a quantity in cases, and the two cannot both be right.
 *
 * Such a line does NOT reconcile — 10 cases x $2.00 is $20, and the invoice
 * says $480 — so `checkLineTotal` already fails it and the Confirm & sync gate
 * already refuses to send it to Inventory or the catalogue. What was missing is
 * WHY. The reviewer got "does not reconcile" and went hunting through three
 * columns; the dangerous repair is to make the arithmetic agree by retyping the
 * Quantity as 240, which reconciles the line and hands the catalogue a per-item
 * price sitting beside a pack count of 24 — a cost 24x too cheap on every
 * future quotation. (The pack-math flag catches that particular repair, but
 * only after the reviewer has already made it.)
 *
 * The test is the one thing that separates this from any other mismatch: the
 * line fails against the PACK count and passes against the ITEM count. Both
 * readings go through the same `checkLineTotal`, so the VAT-inclusive and
 * discounted forms are accepted here exactly as they are anywhere else.
 *
 * Returns null unless the shape is unambiguous — a diagnosis offered on a line
 * that is wrong for some other reason is worse than no diagnosis, because it
 * sends the reviewer somewhere specific and wrong.
 */
export function detectPricePerItem(line: any): PricePerItem | null {
  const m = computeLineMath(line);
  const qty = m.qty;
  const unitPrice = m.unitPrice;

  if (qty === null || unitPrice === null || m.lineTotal === null) return null;
  if (qty <= 0 || unitPrice <= 0) return null;

  const units = num(line?.splitItemUnits ?? line?.itemUnits);
  if (units === null || units <= 0) return null;
  // A line whose pack count is 1 (or is sold loose) prices the same either way,
  // so there is nothing to tell apart and nothing to warn about.
  if (units === qty) return null;
  // A pack cannot hold FEWER than one item, so `units < qty` is not a pricing
  // basis at all — it is a mistyped quantity, and the grid says so in its own
  // words ("Total Units is fewer than the Quantity"). Without this guard a
  // quantity fat-fingered to 10000 against 240 units still passed the item
  // reading below and was diagnosed as a per-item price, recommending a pack
  // price of $0.05. A confident wrong answer, which is the one thing this
  // function must never produce.
  if (units < qty) return null;

  // It must genuinely fail against the packs...
  const packCheck = checkLineTotal(m);
  if (!packCheck || packCheck.ok) return null;

  // ...and genuinely pass against the items. `splitQuantity` is what
  // `lineQuantity` reads first, so overriding it re-reads the whole line as
  // though the quantity had been stated in items.
  const asItems = computeLineMath({ ...line, splitQuantity: units, qty: units, quantity: units });
  const itemCheck = checkLineTotal(asItems);
  if (!itemCheck || !itemCheck.ok) return null;

  // What the pack price would have to be. Derived from the two quantities
  // rather than from `splitPackCount`, which may itself be the misread figure —
  // units/qty is implied by the arithmetic that just passed.
  const unitsPerPack = units / qty;
  const statedPackCount = num(line?.splitPackCount);

  return {
    qty,
    units,
    unitPrice,
    unitsPerPack: roundQty(unitsPerPack),
    impliedPackPrice: round2(unitPrice * unitsPerPack),
    // Present when the line states a pack count that agrees with the two
    // quantities — the reviewer can then be told the number off their own row.
    statedPackCount:
      statedPackCount !== null && roundQty(statedPackCount) === roundQty(unitsPerPack)
        ? statedPackCount
        : null,
    message:
      `Unit Price looks like a price per ITEM, not per pack. ` +
      `${units} items x ${money(unitPrice)} = ${money(m.lineTotal)} matches the Line Total, ` +
      `but ${qty} pack${qty === 1 ? "" : "s"} x ${money(unitPrice)} does not. ` +
      `The Master Catalogue costs one item by dividing this price by Units / Case, so enter ` +
      `the price of a pack (${money(round2(unitPrice * unitsPerPack))}) — or correct the ` +
      `Quantity if this invoice really does bill ${units}.`,
  };
}

/**
 * What this line SHOULD come to under the invoice's own convention, and how far
 * out it is. Positive means the supplier has charged more than their own
 * numbers support — the case the client is losing money on.
 */
export function lineDiscrepancy(
  m: LineMath,
  convention: Convention = "exclusive"
): { expected: number; difference: number } | null {
  if (m.base === null || m.lineTotal === null) return null;
  const expected =
    convention === "inclusive" && m.expectedTotalIncVat !== null ? m.expectedTotalIncVat : m.base;
  return { expected, difference: round2(m.lineTotal - expected) };
}

export type StatedTotals = {
  subTotal: number | null;
  grandTotal: number | null;
  taxTotal: number | null;
  shipping: number | null;
  discountTotal: number | null;
};

/** The tax total to check the lines against: printed, moved by any quantity correction. */
function statedTax(t: any): number | null {
  const printed = num(t.taxTotalAsPrinted);
  if (printed !== null) return round2(printed + (num(t.taxTotalCorrection) ?? 0));
  return num(t.taxTotal ?? t.tax ?? t.vat ?? t.gst ?? t.tax_total);
}

/** The stated totals, under whatever key this invoice printed them. */
export function statedTotals(totals: any): StatedTotals {
  const t = totals && typeof totals === "object" ? totals : {};
  return {
    subTotal: num(t.subTotal ?? t.subtotal ?? t.sub_total),
    grandTotal: num(t.grandTotal ?? t.total ?? t.totalDue ?? t.total_due),
    // `taxTotalAsPrinted` first — it is what the supplier printed. `taxTotal`
    // may since have been rederived from the lines, and checking the lines
    // against a figure derived from those same lines is circular: it agrees by
    // construction and the discrepancy can never fire.
    //
    // A person's quantity correction before stock-in (D44 #19, #46) moves the
    // invoice's tax by what the corrected lines changed; `taxTotalCorrection` is
    // that running change, so the printed tax is judged as corrected — still
    // never a figure derived from the very lines being checked.
    taxTotal: statedTax(t),

    // Freight and invoice-level discount sit BETWEEN the lines and the grand
    // total, so an invoice carrying either fails `subTotal + taxTotal ==
    // grandTotal` no matter how correctly it was read. The extractor has always
    // captured both and nothing consumed them, so a perfectly good invoice with
    // a freight line was flagged, scored down, and — because `hasIssues` gates
    // it — refused by the Inventory confirm gate, with no correction a reviewer
    // could make to clear it.
    //
    // Null, not 0: "no freight printed" and "freight printed as 0.00" are
    // different statements, and only the caller knows which one matters.
    shipping: num(
      t.shipping ?? t.freight ?? t.shippingTotal ?? t.insuranceAndFreight ?? t.delivery
    ),
    discountTotal: num(
      t.discountTotal ?? t.discount_total ?? t.discount ?? t.totalDiscount
    ),
  };
}

export type LineDiscrepancy = {
  index: number;
  lineNo: any;
  description: string;
  math: LineMath;
  check: LineTotalCheck;
  expected: number;
  actual: number;
  difference: number;
  /** Set when the gap is a pricing BASIS, not money owed — see the push site. */
  pricePerItem: PricePerItem | null;
};

export type AddendaSuspect = {
  /** 'equals-grand' | 'balances-without' | 'occupies-tax' */
  code: string;
  field: string;
  reason: string;
};

export type AddendaAudit = {
  shipping: number | null;
  discountTotal: number | null;
  addenda: number;
  suspect: AddendaSuspect | null;
};

/**
 * Judge a freight figure against the rest of the totals.
 *
 * Freight is the one money field on these invoices with no corroboration
 * anywhere else on the document: a line total can be checked against qty x
 * price, a tax amount against the rate, a subtotal against the lines. Freight
 * is a single number in a box, and the extractor reads it off the faintest
 * region of a scan. Of the freight figures in QA at the time of writing, not
 * one was real:
 *
 *   - Blue Waters PSINV0280871 printed FREIGHT 0.00 and TAX 16.00, with the
 *     tax row double-struck so its top and bottom halves straddled the two
 *     rows. The model read the lower half as the tax (16, right) and the upper
 *     half as freight ("$4...", wrong). 2528 + 16 already equalled the printed
 *     2544, so there was no room for a freight charge at all — but 4 is 0.16%
 *     of the total, and the 1% band swallowed it in silence.
 *   - National Canners N0196936 printed FREIGHT 0.00 and VAT 89.75. Those two
 *     rows were swapped, so the VAT was filed as freight and the tax total
 *     recorded as zero. The arithmetic still balanced perfectly — the money
 *     was all present, just in the wrong box — so no check could ever see it.
 *   - BINV00307341 carried a freight equal to its own grand total.
 *
 * Each of those has a signature, and this is where they are named. The verdict
 * is a SUSPICION, not a correction: the reviewer confirms it against the paper.
 * What it does decide outright is whether the figure may go on influencing the
 * arithmetic — a freight the invoice has no room for must not keep quietly
 * eating the tolerance budget that hid it in the first place.
 */
export function auditAddenda(stated: any): AddendaAudit {
  const shipping = num(stated && stated.shipping);
  const discountTotal = num(stated && stated.discountTotal);
  const subTotal = num(stated && stated.subTotal);
  const taxTotal = num(stated && stated.taxTotal);
  const grandTotal = num(stated && stated.grandTotal);

  let suspect: AddendaSuspect | null = null;

  if (shipping !== null && shipping !== 0 && grandTotal !== null) {
    if (Math.abs(shipping - grandTotal) <= 0.01) {
      // The grand total copied into the freight box. Nothing is being charged
      // twice; one figure has been read into two fields.
      suspect = {
        code: "equals-grand",
        field: "shipping",
        reason:
          `Freight is ${money(shipping)}, which is this invoice's entire grand total. That is the ` +
          `total read into the freight box by mistake, not a delivery charge. Check the document ` +
          `and clear it if the invoice prints no freight.`,
      };
    } else if (
      subTotal !== null &&
      taxTotal !== null &&
      // TOL_ABS (cent rounding), NOT tolerance(grandTotal): the 1% band is a
      // rounding budget for line arithmetic, and here it declared any freight
      // under 1% of the grand total phantom — 500.00 on a 100,500.00 invoice
      // "balanced without it" by 500 inside a 1,005 band. "Closes without it"
      // has to mean EXACTLY, to the cent (CodeRabbit, 2026-08-21).
      Math.abs(subTotal + taxTotal - grandTotal) <= TOL_ABS
    ) {
      // Decisive: the invoice closes without it. Whatever the ink looked like,
      // there is no room in this document's own arithmetic for a freight charge.
      suspect = {
        code: "balances-without",
        field: "shipping",
        reason:
          `Freight reads ${money(shipping)}, but this invoice already adds up without it — ` +
          `${money(subTotal)} + ${money(taxTotal)} tax = ${money(grandTotal)}, its own printed ` +
          `total. A freight charge would have to make the total larger. It is being ignored in ` +
          `the checks below; clear it, or correct the figure it was misread from.`,
      };
    } else if (
      (taxTotal === null || taxTotal === 0) &&
      subTotal !== null &&
      Math.abs(subTotal + shipping - grandTotal) <= 0.02
    ) {
      // The swap. Arithmetically perfect and therefore invisible to every
      // other check here: the money is all present, it is just in the wrong
      // box. Only a person looking at the document can settle it.
      suspect = {
        code: "occupies-tax",
        field: "shipping",
        reason:
          `This invoice records no tax and a freight of ${money(shipping)}, which is exactly what ` +
          `closes the gap to its grand total. On a scanned totals block the FREIGHT and VAT rows ` +
          `sit next to each other and are easily swapped — check the document: if ${money(shipping)} ` +
          `is the VAT, it belongs in Tax Total, not Freight.`,
      };
    }
  }

  // A freight the invoice has no room for must not go on influencing the sums.
  // `occupies-tax` is excluded from this: there the money IS part of the total,
  // merely filed under the wrong heading, and dropping it would break a
  // reconciliation that currently holds.
  const countShipping = suspect === null || suspect.code === "occupies-tax";
  const addenda = round2((countShipping ? shipping || 0 : 0) - (discountTotal || 0));

  return { shipping, discountTotal, addenda, suspect };
}

export type InvoiceReconciliation = {
  convention: Convention;
  discrepancies: LineDiscrepancy[];
  computedSubTotal: number;
  computedTaxTotal: number;
  computedGrandTotal: number;
  stated: StatedTotals;
  anyLineStatesTax: boolean;
  /** The line totals already hold the VAT the foot states (see linesHoldFootVat). */
  vatInLineTotals: boolean;
  overchargeTotal: number;
  overchargeLines: number;
  underchargeTotal: number;
  underchargeLines: number;
};

/**
 * Do this invoice's line totals already hold the VAT its foot states?
 *
 * The one shape the lines cannot answer for themselves (see the use in
 * reconcileInvoiceLines): no line states a rate or an amount, so qty x price =
 * line total reads the same with VAT inside or outside. The foot decides, and
 * only when it is unambiguous:
 *   - it states VAT above zero, less than the lines, and a grand total;
 *   - the lines (with freight/discount, as auditAddenda keeps them) come to
 *     that grand total, within tolerance AND closer than lines + VAT do: an
 *     exclusive invoice with a small VAT figure stays exclusive;
 *   - a printed subtotal, if any, is the lines less that VAT. A grand total
 *     misread as the subtotal (subtotal 400, VAT 50, "grand" 400, lines 400)
 *     fails this, so it stays a refusal instead of passing. With no subtotal
 *     printed there is nothing to catch that misread; the review screen says
 *     "Read as VAT-inclusive" so the reviewer sees the reading taken.
 * A line that states tax keeps the per-line vote (`invoiceConvention`).
 */
export function linesHoldFootVat(linesTotal: number, anyLineStatesTax: boolean, stated: StatedTotals): boolean {
  const tax = stated?.taxTotal;
  const grand = stated?.grandTotal;
  if (anyLineStatesTax || tax === null || tax === undefined || grand === null || grand === undefined) return false;
  if (!(tax > 0) || !(tax < linesTotal)) return false;
  const { addenda } = auditAddenda(stated);
  const offAsInclusive = Math.abs(round2(linesTotal + addenda) - grand);
  const offAsExclusive = Math.abs(round2(linesTotal + addenda + tax) - grand);
  if (offAsInclusive > tolerance(grand) || offAsInclusive >= offAsExclusive) return false;
  const sub = stated.subTotal;
  return sub === null || sub === undefined || Math.abs(round2(linesTotal - tax) - sub) <= tolerance(sub);
}

/**
 * Reconcile a whole invoice against its own lines.
 *
 * This is the single source of truth for "do the numbers on this invoice add
 * up, and is anyone being overcharged". The server stores the verdict; the grid
 * recomputes it live from the values on screen. Same function, so they cannot
 * disagree.
 */
export function reconcileInvoiceLines(extracted: any): InvoiceReconciliation {
  const ex = extracted && typeof extracted === "object" ? extracted : {};
  const items: any[] = Array.isArray(ex.lineItems) ? ex.lineItems : [];
  const stated = statedTotals(ex.totals);

  const convention = invoiceConvention(items);
  const discrepancies: LineDiscrepancy[] = [];

  let computedSubTotal = 0;
  let computedTaxTotal = 0;
  let anyLineStatesTax = false;

  items.forEach((li, index) => {
    const m = computeLineMath(li);
    const check = checkLineTotal(m);
    const vat = lineVat(m);

    if (lineStatesTax(m)) anyLineStatesTax = true;

    if (check && !check.ok) {
      const gap = lineDiscrepancy(m, convention);
      if (gap && gap.difference !== 0) {
        // A line priced per ITEM against a quantity in PACKS is not an
        // overcharge, and reporting one is an accusation against a supplier who
        // has billed correctly. INV/2026/17367: 2 cases of 12, priced per
        // piece, extended to 24 x the piece price — the invoice is right in
        // every particular, and this recorded a $110 overcharge on it because
        // `lineDiscrepancy` measured the printed total against 2 x the piece
        // price. `overchargeTotal` is persisted on the document and read by
        // accounts payable, so the figure did not stay on the review screen.
        //
        // It stays a DISCREPANCY: the line still needs a person, because the
        // Master Catalogue derives a per-item cost by dividing Unit Price by
        // Units / Case and would come out 12x too cheap. What it is not is a
        // money claim against the vendor, so it is kept out of the over/under
        // sums below.
        const perItem = detectPricePerItem(li);

        discrepancies.push({
          index,
          lineNo: li?.lineNo === undefined || li?.lineNo === null ? index + 1 : li.lineNo,
          description: String(li?.itemName || li?.description || "").slice(0, 60),
          math: m,
          check,
          expected: gap.expected,
          actual: m.lineTotal as number,
          difference: gap.difference,
          /** Set when the gap is a pricing BASIS, not money owed. */
          pricePerItem: perItem || null,
        });
      }
    }

    // Prefer the printed extension — it is what the supplier is charging — and
    // fall back to qty x price only where the line prints no total at all.
    const isVatInclusive =
      !!check &&
      check.ok &&
      m.expectedTotalIncVat !== null &&
      check.expected === m.expectedTotalIncVat &&
      m.expectedTotalIncVat !== m.base;

    if (m.lineTotal !== null) {
      computedSubTotal += isVatInclusive ? m.lineTotal - vat : m.lineTotal;
    } else if (m.base !== null) {
      computedSubTotal += m.base;
    }
    computedTaxTotal += vat;
  });

  computedSubTotal = round2(computedSubTotal);
  computedTaxTotal = round2(computedTaxTotal);

  // VAT ALREADY INSIDE THE LINES (D106; QA 2026-10-08, Portside Marine Traders
  // INV-PMT-5001/5002; owner: detect it from the totals, and say so on screen).
  // A supplier that prints every price and line total WITH VAT and states the
  // VAT only at the foot ("VAT included 50.00") looks, line by line, exactly
  // like an exclusive one: qty x price = line total either way, and no line
  // states a rate. Only the foot tells them apart. The lines alone ARE the
  // grand total, so adding the foot's VAT on top (the branch below) counts it
  // twice and refuses a correct invoice.
  const vatInLineTotals = linesHoldFootVat(computedSubTotal, anyLineStatesTax, stated);
  if (vatInLineTotals) {
    computedTaxTotal = round2(stated.taxTotal as number);
    computedSubTotal = round2(computedSubTotal - computedTaxTotal);
  }

  // The grand total the lines support.
  //
  // When NO line says anything about tax, the lines are silent on the subject
  // rather than asserting zero — which is the ordinary Trinidad shape, where VAT
  // is added once at the foot and never printed per line. Adding 0 of tax and
  // then complaining that the result misses the printed grand total by exactly
  // the printed tax raised a discrepancy on every correctly-read invoice of that
  // shape. The invoice's own tax total is the honest figure to use there; the
  // subtotal + tax = grand check is what actually tests it.
  const effectiveTax = anyLineStatesTax || vatInLineTotals
    ? computedTaxTotal
    : stated.taxTotal !== null
    ? stated.taxTotal
    : 0;
  const computedGrandTotal = round2(computedSubTotal + effectiveTax);

  // A basis mismatch is excluded from BOTH sums. The gap it produces is an
  // artifact of reading the price column in the wrong unit, and it is large —
  // the full difference between one pack and one item — so leaving it in
  // dominates the figure accounts payable reads and buries any real overcharge
  // on the same invoice.
  const moneyGaps = discrepancies.filter((d) => !d.pricePerItem);
  const over = moneyGaps.filter((d) => d.difference > 0);
  const under = moneyGaps.filter((d) => d.difference < 0);

  return {
    convention: vatInLineTotals ? "inclusive" : convention,
    discrepancies,
    computedSubTotal,
    computedTaxTotal,
    computedGrandTotal,
    stated,
    anyLineStatesTax,
    vatInLineTotals,
    overchargeTotal: round2(over.reduce((acc, d) => acc + d.difference, 0)),
    overchargeLines: over.length,
    underchargeTotal: round2(under.reduce((acc, d) => acc + Math.abs(d.difference), 0)),
    underchargeLines: under.length,
  };
}
